import React from "react";

/**
 * Full Tailwind class strings per tone, never `bg-bc-${tone}`.
 *
 * Tailwind scans source text for literal class names; a template-built class
 * is not in the stylesheet at all, so the fill would ship transparent.
 * @type {Record<string, string>}
 */
const FILL_CLASSES = {
  push: "bg-orange-primary",
  email: "bg-bc-email",
};

/**
 * Clamp anything to a sane 0..100. Null, undefined and NaN mean "no value",
 * which renders as an EMPTY track — a full bar would assert 100%.
 *
 * @param {number|null|undefined} pct
 * @returns {number}
 */
const clampPct = (pct) => {
  const value = Number(pct);
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
};

/**
 * The funnel progress bar.
 *
 * ACCESSIBILITY: the bar is purely DECORATIVE and carries `aria-hidden`. The
 * spec is explicit — "Funnel bars are decorative; the value is always adjacent
 * as text." Giving it a `progressbar` role would make a screen reader read the
 * same percentage twice, once as a bare number with no unit or subject. Reading
 * it once, from the adjacent label, is strictly better than reading it twice.
 *
 * @param {Object} props
 * @param {number|null|undefined} props.pct - 0..100. Out-of-range clamps; absent renders empty.
 * @param {'push'|'email'} [props.tone='push'] - Which channel this bar belongs to.
 * @param {string} [props.className] - Extra classes on the track.
 */
export const Bar = ({ pct, tone = "push", className = "" }) => {
  const fillClass = FILL_CLASSES[tone] || FILL_CLASSES.push;

  return (
    <div
      aria-hidden="true"
      className={`h-[5px] rounded-full bg-bc-hairline overflow-hidden ${className}`}
    >
      {/* A percentage cannot be a Tailwind class, so the width is inline. */}
      <div className={`h-full rounded-full ${fillClass}`} style={{ width: `${clampPct(pct)}%` }} />
    </div>
  );
};
